import React, { useEffect, useState } from 'react';
import agent from '../../services/Api/agent';
import { TidalDto } from '../../models/Weather';

interface TidalDisplayContainerProps {
    station: string;
    stationName?: string;
    autoRefresh?: boolean;
    refreshInterval?: number; // in milliseconds, default 5 minutes
}

export default function TidalDisplayContainer({
    station,
    stationName,
    autoRefresh = true,
    refreshInterval = 300000 // 5 minutes default
}: TidalDisplayContainerProps) {
    const [tidalData, setTidalData] = useState<TidalDto | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

    const fetchTidalData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await agent.Weather.getTidalData(station);
            setTidalData(data);
            setLastUpdate(new Date());
        } catch (err: any) {
            console.error('Error fetching tidal data:', err);
            setError(err.message || 'Failed to fetch tidal data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Initial fetch
        fetchTidalData();

        // Set up auto-refresh if enabled
        let intervalId: NodeJS.Timeout | null = null;
        if (autoRefresh) {
            intervalId = setInterval(() => {
                fetchTidalData();
            }, refreshInterval);
        }

        // Cleanup interval on unmount
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [station, autoRefresh, refreshInterval]);

    const formatDateTime = (dateTimeStr: string): string => {
        try {
            // Parse the dateTime string (format: "2025-12-22 15:04")
            const [datePart, timePart] = dateTimeStr.split(' ');
            if (datePart && timePart) {
                const [year, month, day] = datePart.split('-');
                const [hour, minute] = timePart.split(':');
                const date = new Date(
                    parseInt(year),
                    parseInt(month) - 1,
                    parseInt(day),
                    parseInt(hour),
                    parseInt(minute)
                );
                return date.toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                });
            }
            return dateTimeStr;
        } catch {
            return dateTimeStr;
        }
    };

    const getNumericValue = (value: number | string): number => {
        if (typeof value === 'number') return value;
        const num = parseFloat(String(value));
        return isNaN(num) ? 0 : num;
    };

    if (loading && !tidalData) {
        return (
            <div className="bg-white rounded-xl border-2 border-gray-200 shadow-md p-6">
                <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Loading...</span>
                </div>
            </div>
        );
    }

    if (error && !tidalData) {
        return (
            <div className="bg-white rounded-xl border-2 border-red-200 shadow-md p-6">
                <div className="flex items-center justify-center h-32">
                    <div className="text-red-600 text-center">
                        <p className="font-semibold">Failed to Load</p>
                        <p className="text-sm mt-1">{error}</p>
                        <button
                            onClick={fetchTidalData}
                            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!tidalData) {
        return null;
    }

    return (
        <div className="bg-white rounded-xl border-2 border-gray-200 shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-white text-xl font-bold">
                            {'Hong Kong International Airport - Actual Water Level'}
                            {/* {stationName || `Tidal Station ${station}`} */}
                        </h3>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                        {autoRefresh && (
                            <span className="text-xs text-white">
                                Auto Refresh: Every {Math.floor(refreshInterval / 60000)} minutes
                            </span>
                        )}
                        <button
                            onClick={fetchTidalData}
                            disabled={loading}
                            className="px-3 py-2 bg-white hover:bg-gray-100 text-blue-600 rounded-lg border-2 border-white hover:border-blue-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center"
                            title="Refresh Data"
                        >
                            <svg
                                className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-6">
                {/* DateTime */}
                <div>
                    <div className="flex">
                        <div>
                            <p className="text-gray-500 text-sm mb-1">Record Time</p>
                            <p className="text-gray-900 text-lg font-semibold">
                                {formatDateTime(tidalData.dateTime)}
                            </p>
                            {lastUpdate && (
                                <p className="text-gray-400 text-xs mt-1">
                                    Last Updated: {lastUpdate.toLocaleTimeString('en-US')}
                                </p>
                            )}
                        </div>


                        {/* Data Grid */}
                        <div className="ml-auto">
                            <div className="bg-blue-50 rounded-lg p-6 border-2 border-blue-300 shadow-md">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-blue-700 text-sm font-medium mb-2">Actual Water Level</p>
                                        <p className="text-blue-900 text-5xl font-bold">
                                            {(getNumericValue(tidalData.realData) - 0.146).toFixed(2) }
                                            <span className="text-lg text-blue-700 ml-1">mPD</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

